import { useRef, useEffect, useState } from 'react';
import { Sparkles, Loader } from 'lucide-react';
import { api } from '../../api/client';
import { useToast } from '../../store/toastStore';
import type { Pipeline } from '../../store/pipelineStore';

interface Props {
  value: string;
  onChange: (v: string) => void;
  onSave: () => void;
  isSaving: boolean;
  pipeline: Pipeline | null;
  startupId: string;
}

// FIX: BUG 4 - format objects like {minimum:"...", good:"...", exceptional:"..."} as readable text
function stringify(v: unknown): string {
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) {
    return v.map(item => stringify(item)).join('; ');
  }
  if (typeof v === 'object' && v !== null) {
    const obj = v as Record<string, unknown>;
    // Format key-value objects as "Key: value" lines
    const entries = Object.entries(obj).filter(([, val]) => val != null && val !== '');
    if (entries.length === 0) return '';
    // If all values are simple strings/numbers, format as readable key-value pairs
    const allSimple = entries.every(([, val]) => typeof val === 'string' || typeof val === 'number');
    if (allSimple) {
      return entries.map(([key, val]) => {
        const label = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
        return `${label}: ${val}`;
      }).join('\n');
    }
    // Fallback: try common named fields, then readable key-value
    const named = obj.title || obj.text || obj.name || obj.label;
    if (typeof named === 'string') return named;
    return entries.map(([key, val]) => {
      const label = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
      return `${label}: ${stringify(val)}`;
    }).join('\n');
  }
  return String(v);
}

function buildClientSideSummary(pipeline: Pipeline): string {
  const sections: string[] = [];
  const ai = (key: keyof Pipeline, field: string): unknown => {
    const step = pipeline[key] as { aiOutput?: Record<string, unknown> } | undefined;
    return step?.aiOutput?.[field];
  };

  // Key Learnings
  const learnings: string[] = [];
  const whatWorks = ai('paymentTest', 'whatWorks');
  if (whatWorks) learnings.push(stringify(whatWorks));
  const successThreshold = ai('validationPath', 'successThreshold');
  if (successThreshold) learnings.push(stringify(successThreshold));
  if (learnings.length > 0) {
    sections.push('Key Learnings\n' + learnings.map(l => `• ${l}`).join('\n'));
  }

  // Risks & Mitigations
  const risks: string[] = [];
  const biggestThreat = ai('realityCheck', 'biggestThreat');
  if (biggestThreat) risks.push(`Biggest risk: ${stringify(biggestThreat)}`);
  const warningSignals = ai('paymentTest', 'warningSignals');
  if (warningSignals) risks.push(stringify(warningSignals));
  if (risks.length > 0) {
    sections.push('Risks & Mitigations\n' + risks.map(r => `• ${r}`).join('\n'));
  }

  // MVP Plan
  const mvpParts: string[] = [];
  const coreFeature = ai('mvpBlueprint', 'coreFeature');
  if (coreFeature) mvpParts.push(stringify(coreFeature));
  const timeline = ai('mvpBlueprint', 'timeline');
  if (timeline) {
    mvpParts.push(`Timeline: ${stringify(timeline)}`);
  }
  if (mvpParts.length > 0) {
    sections.push('MVP Plan\n' + mvpParts.map(p => `• ${p}`).join('\n'));
  }

  // Next Milestones
  const nextSteps = ai('mvpBlueprint', 'nextStepsAfterMVP');
  if (nextSteps) {
    const steps = Array.isArray(nextSteps)
      ? (nextSteps as unknown[]).slice(0, 3).map(s => stringify(s))
      : [stringify(nextSteps)];
    sections.push('Next Milestones\n' + steps.map(s => `• ${s}`).join('\n'));
  }

  // Use of Funds
  const budget = ai('mvpBlueprint', 'budgetEstimate');
  if (budget) {
    sections.push(`Use of Funds\n• Budget estimate: ${stringify(budget)}`);
  }

  if (sections.length === 0) {
    return 'Complete your validation pipeline to auto-generate a detailed founder summary.';
  }

  return sections.join('\n\n');
}

export function FounderSummaryEditor({ value, onChange, onSave, isSaving, pipeline, startupId }: Props) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(el.scrollHeight, 160)}px`;
  }, [value]);

  const handleAutoGenerate = async () => {
    setIsGenerating(true);
    try {
      const { data } = await api.post(`/dashboard/${startupId}/summary/generate`);
      const generated = data.data?.summary ?? data.data?.text ?? null;
      if (generated) {
        onChange(generated);
        toast('Summary generated! Review and edit before saving.', 'success');
      } else {
        throw new Error('empty_response');
      }
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const isNotImpl = status === 404 || status === 501 || status === 503 || (err as Error)?.message === 'empty_response';

      if (isNotImpl && pipeline) {
        // Build client-side from pipeline data
        const generated = buildClientSideSummary(pipeline);
        onChange(generated);
        setTimeout(() => {
          textareaRef.current?.focus();
          textareaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 50);
        toast('Summary drafted from your pipeline data — review and edit before saving.', 'success');
      } else if (isNotImpl && !pipeline) {
        toast('No pipeline data found. Complete your validation pipeline first.', 'info');
      } else {
        toast('Failed to generate summary. Please try again.', 'error');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 4, gap: 12 }}>
        <div>
          <h2 className="text-lg font-bold text-white">Founder's Summary</h2>
          <p className="text-gray-500 text-xs mt-1">Visible in PDF export. Write for investors.</p>
        </div>
        <button
          onClick={handleAutoGenerate}
          disabled={isGenerating}
          style={{
            background: isGenerating ? '#2a2a3e' : 'rgba(124,111,247,0.15)',
            border: '1px solid rgba(124,111,247,0.35)',
            borderRadius: 8, color: '#7c6ff7',
            fontSize: 12, fontWeight: 600, padding: '6px 14px', cursor: isGenerating ? 'default' : 'pointer',
            whiteSpace: 'nowrap', flexShrink: 0,
          }}
        >
          {isGenerating ? <><Loader size={14} className="animate-spin" /> Generating…</> : <><Sparkles size={14} /> Auto-Generate</>}
        </button>
      </div>

      <textarea
        ref={textareaRef}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={`Key learnings from beta testing...\nMain risks and mitigations...\nUse of funds...\nNext milestones...`}
        className="input-field font-mono text-sm mt-3"
        style={{ resize: 'none', minHeight: 160, overflow: 'hidden' }}
      />

      <button
        onClick={onSave}
        disabled={isSaving}
        className="btn-primary mt-3 text-sm"
      >
        {isSaving ? 'Saving…' : 'Save Summary'}
      </button>
    </div>
  );
}
